import { useState, useEffect } from 'react';

import { BannerError } from '../types';
import { ErrorContext } from './error-context';
import { useGetBanner } from '../../../hooks/query/content-api/use-banner';

// ----------------------------------------------------------------------------

const DEFAULT_BANNER_ERROR: BannerError = {
  estimatedFixTime: null,
  message:
    'We are currently trying to fix an issue which is causing our product to work incorrectly.',
  status: 'Error',
};

interface ErrorState {
  code: number | null;
  message: string;
}

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<ErrorState | null>(null);
  const [bannerError, setBannerError] = useState<BannerError | null>(null);

  const {
    data: bannerData,
    isLoading: isBannerLoading,
    isError: isBannerError,
  } = useGetBanner({
    refetchInterval: 300_000, // 5 minutes
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (isBannerLoading) {
      setBannerError(null);
      return;
    }

    if (isBannerError) {
      setBannerError(DEFAULT_BANNER_ERROR);
      return;
    }

    const banners = bannerData?.body?.banners;
    const firstBanner = banners?.[0];

    if (firstBanner?.showMessage) {
      setBannerError({
        estimatedFixTime: firstBanner.estimatedFixTime,
        message: firstBanner.message,
        status: firstBanner.status,
      });
    } else {
      setBannerError(null);
    }
  }, [isBannerError, bannerData, isBannerLoading]);

  // ------------------------------------------------

  return (
    <ErrorContext.Provider
      value={{
        error,
        setError,
        bannerError,
        setBannerError,
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
}
